// Every external system this person exists in, on one line under their name.
//
// These links all existed already, in four different places: Crisp in the user page
// header, both Stripe links buried inside UserDetailsCard's collapsed accordion, and
// Checkr reachable only from the driver profile's screening card. Four systems, four
// places to remember — which is most of why support kept three tabs open.
//
// Real vendor marks, replacing the monogram badges. Each is the vendor's own asset:
// Stripe's is the single-colour glyph, Crisp's and Checkr's are their site favicons.
//
// Retool is gone. It was in here only because it happened to be on the page already, and
// the user detail app it linked to is what this page replaces.
import React from 'react';
import { Box, Stack, Typography } from '../../ui/core';
import { OpenInNewIcon } from '../../ui/icons';
import config from '../../config';
import { crispContactUrl, openCrispContact } from '../../components/widgets/Users/UserCrispLink';

import CrispLogo from '../../assets/crisp-logo.png';
import StripeLogo from '../../assets/stripe-logo.svg';
import CheckrLogo from '../../assets/checkr-logo.png';

const ServiceLink = ({ name, href, hint, logo, onClick }) => (
  <Stack
    component="a"
    href={href}
    onClick={onClick}
    target="_blank"
    rel="noreferrer"
    direction="row"
    alignItems="center"
    spacing={0.6}
    title={hint ? `${name} · ${hint}` : name}
    sx={{
      textDecoration: 'none',
      color: 'text.secondary',
      flexShrink: 0,
      '&:hover': { color: 'text.primary', '& .svc-name': { textDecoration: 'underline' } },
    }}
  >
    {/* A white tile behind every mark, not just the ones that need it. Vendor logos are
        drawn to sit on white — Checkr's is a near-black tile that all but disappears on
        this header, and Stripe's glyph is a single dark-ish purple. Backing them
        individually would give the row three different treatments; backing all of them is
        the same thing a browser tab strip does. */}
    <Box
      component="img"
      src={logo}
      alt=""
      sx={{
        width: 15,
        height: 15,
        borderRadius: '4px',
        flexShrink: 0,
        backgroundColor: '#fff',
        // The marks are square with their own padding, except Stripe's glyph which is a
        // bare S — a little inset stops it touching the tile edge.
        p: '1px',
        objectFit: 'contain',
      }}
    />
    <Typography className="svc-name" sx={{ fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap' }}>
      {name}
    </Typography>
    <OpenInNewIcon sx={{ fontSize: 11, opacity: 0.5 }} />
  </Stack>
);

/**
 * @param user           the Mongo user document
 * @param userRawUnsafe  the parsed raw document — the only place the Stripe *connected
 *                       account* id lives, which is why it is a separate argument
 * @param candidateId    the driver's Checkr candidate id, absent for non-drivers and
 *                       for drivers who have never been screened
 */
const ServiceStrip = ({ user, userRawUnsafe, candidateId }) => {
  // The href is the phone-number search — see crispContactUrl, which owns that rule so
  // this does not get to guess at it. A plain click resolves the exact Crisp contact
  // through the admin API first and only falls back to the search.
  const crisp = crispContactUrl({
    userId: user?.id,
    phoneNumber: user?.phoneNumber || userRawUnsafe?.phoneNumber,
  });

  // Each entry is dropped when its id is missing rather than rendered dead. A link to
  // nothing is worse than no link — it reads as "this exists, and is broken".
  const links = [
    crisp && {
      name: 'Crisp',
      hint: 'contact profile, or search by phone number',
      href: crisp,
      onClick: openCrispContact({ userId: user?.id, fallbackUrl: crisp }),
      logo: CrispLogo,
    },
    user?.customer_id && {
      name: 'Stripe customer',
      hint: user.customer_id,
      href: `https://dashboard.stripe.com/customers/${user.customer_id}`,
      logo: StripeLogo,
    },
    userRawUnsafe?.account_id && {
      name: 'Stripe connect',
      hint: userRawUnsafe.account_id,
      href: `https://dashboard.stripe.com/connect/accounts/${userRawUnsafe.account_id}`,
      logo: StripeLogo,
    },
    candidateId && {
      name: 'Checkr',
      hint: candidateId,
      // Same base as the driver profile's screening card — staging and production are
      // different Checkr tenants, so this must come from config, never a literal.
      href: `${config.checkr.baseUrl.trim()}/candidates/${candidateId}`,
      logo: CheckrLogo,
    },
  ].filter(Boolean);

  if (!links.length) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ flexWrap: 'wrap', rowGap: 0.5, columnGap: { xs: 1.75, sm: 0 } }}
    >
      {links.map((l, i) => (
        <React.Fragment key={l.name}>
          {/* A real divider glyph, not a border: these read as a sentence of links, and
              spacing alone stopped separating them once there were four.

              Hidden below sm. Four links do not fit one phone row, so the strip wraps to
              one link per line — and a separator that was between two links then leads
              the next line, leaving a column of stray pipes. Column gap carries the
              separation there instead. */}
          {i > 0 && (
            <Typography
              sx={{
                fontSize: '12px',
                color: 'text.disabled',
                mx: 1.25,
                userSelect: 'none',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              |
            </Typography>
          )}
          <ServiceLink {...l} />
        </React.Fragment>
      ))}
    </Stack>
  );
};

export default ServiceStrip;
